import { CSSObject, debounce, styled, Theme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';

import { calizaApi as api, useCreateTransactionMutation, useGetBeneficiariesQuery } from '@/api/v1';
import ExpandIcon from '@/assets/icons/dropdown-arrow.svg?react';
import { HighlightedText } from '@/components/atoms/HighlightedText';
import { ExpectedSettlementTimeInfo } from '@/components/molecules/ExpectedSettlementTimeInfo';
import { FullScreenModalContainer } from '@/components/molecules/FullScreenModalContainer';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { SearchMenuItem } from '@/components/molecules/SearchMenuItem';
import { Section } from '@/components/molecules/Section';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationDialog } from '@/components/organisms/ApplicationDialog';
import { Header } from '@/components/organisms/Header';
import { TransactionDetails } from '@/components/organisms/TransactionDetails';
import { Currency } from '@/data/currencies/type';
import { useBalances } from '@/hooks/useBalances';
import { FilteredBeneficiary, useBeneficiarySearch } from '@/hooks/useBeneficiarySearch';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useCustomer } from '@/hooks/useCustomer';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useSimulation } from '@/hooks/useSimulation';
import { useToast } from '@/hooks/useToast';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import {
  beneficiariesLoaded,
  currencyChanged,
  customerBeneficiaryChanged,
  directionChanged,
  fromAmountChanged,
  getDestinationBeneficiaryId,
  getSourceBeneficiaryId,
  initialState,
  InternalTransferAction,
  InternalTransferDirection,
  InternalTransferFlowState,
  internalTransferReducer,
  mainBeneficiarySet,
  simulationCreated,
  toAmountChanged,
} from '@/Screens/Dashboard/Flows/InternalTransferFlow/reducer';
import { InternalTransferLocationState } from '@/Screens/Dashboard/Flows/locationState';
import { DetailedBalance } from '@/types/bankInfo';
import { Beneficiary } from '@/types/beneficiary';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { Locale } from '@/types/locale';
import {
  ExpectedSettlementTimeDetails,
  ExpectedSettlementTimeType,
  MarketExecutionType,
  TransactionResponse,
} from '@/types/transaction';
import { AsAnyActionArg } from '@/utility/reducers';
import { toBeneficiary } from '@/utility/toBeneficiary';

enum FormErrors {
  MISSING_DIRECTION = 'MISSING_DIRECTION',
  MISSING_CUSTOMER = 'MISSING_CUSTOMER',
  MISSING_CURRENCY = 'MISSING_CURRENCY',
  MISSING_AMOUNT = 'MISSING_AMOUNT',
  AMOUNT_BIGGER_THAN_BALANCE = 'AMOUNT_BIGGER_THAN_BALANCE',
}

const SUPPORTED_CURRENCIES: readonly CurrencyCodeType[] = [
  CurrencyCodeType.USD,
  CurrencyCodeType.USDC,
  CurrencyCodeType.USDT,
];

const INSTANT_SETTLEMENT: ExpectedSettlementTimeDetails = {
  expectedTime: ExpectedSettlementTimeType.INSTANT,
  displayText: 'Instant',
};

export const InternalTransferFlow: React.FC = (): React.ReactElement => {
  const reducer = React.useReducer<
    InternalTransferFlowState,
    AsAnyActionArg<InternalTransferAction>
  >(internalTransferReducer, initialState);
  const [state, dispatch] = reducer;
  const {
    direction,
    customerBeneficiaryId,
    currency,
    fromAmount,
    toAmount,
    beneficiaries,
    mainBeneficiaryId,
  } = state;

  const navigate = useNavigate();
  const location = useLocation();
  const tr = useTranslator();
  const toast = useToast();
  const customer = useCustomer();
  const formatter = useCurrencyFormatter();
  const globalDispatch = useDispatch();
  const locale = React.useContext<Locale>(LocaleContext);
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();

  const [error, setError] = React.useState<FormErrors | null>(null);

  const { data: fetchedBeneficiariesPage } = useGetBeneficiariesQuery();
  const fetchedBeneficiaries = React.useMemo(
    (): readonly Beneficiary[] => (fetchedBeneficiariesPage?.content ?? []).map(toBeneficiary),
    [fetchedBeneficiariesPage],
  );

  React.useEffect((): void => {
    if (!customer.beneficiaryId) {
      return;
    }
    if (mainBeneficiaryId !== customer.beneficiaryId) {
      dispatch(mainBeneficiarySet(customer.beneficiaryId));
    }
    if (fetchedBeneficiaries.length > 0 && beneficiaries.length === 0) {
      dispatch(beneficiariesLoaded(fetchedBeneficiaries));
    }
  }, [
    customer.beneficiaryId,
    mainBeneficiaryId,
    fetchedBeneficiaries,
    beneficiaries.length,
    dispatch,
  ]);

  const presetState = location.state as InternalTransferLocationState | null;
  const presetAppliedRef = React.useRef(false);
  React.useEffect((): void => {
    if (presetAppliedRef.current || !presetState) {
      return;
    }
    if (presetState.presetDirection) {
      dispatch(directionChanged(presetState.presetDirection));
    }
    if (presetState.presetCustomerBeneficiaryId) {
      dispatch(customerBeneficiaryChanged(presetState.presetCustomerBeneficiaryId));
    }
    presetAppliedRef.current = true;
  }, [dispatch, presetState]);

  const customerBeneficiaries = React.useMemo(
    (): readonly Beneficiary[] =>
      beneficiaries.filter((b: Beneficiary): boolean => b.id !== mainBeneficiaryId),
    [beneficiaries, mainBeneficiaryId],
  );

  const { filteredBeneficiaries, onSearchKeywordChange } =
    useBeneficiarySearch(customerBeneficiaries);

  const sourceBeneficiaryId = React.useMemo((): string => getSourceBeneficiaryId(state), [state]);
  const destinationBeneficiaryId = React.useMemo(
    (): string => getDestinationBeneficiaryId(state),
    [state],
  );

  const sourceBalances = useBalances({
    beneficiaryId: sourceBeneficiaryId || undefined,
    currencies: SUPPORTED_CURRENCIES,
  });

  const currentBalance: DetailedBalance | undefined =
    currency !== CurrencyCodeType.UNSET ? sourceBalances[currency] : undefined;

  const onDirectionChange = React.useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, value: string): void => {
      dispatch(directionChanged(value as InternalTransferDirection));
      setError(null);
    },
    [dispatch],
  );

  const onCustomerChange = React.useCallback(
    (event: SelectChangeEvent<string>): void => {
      dispatch(customerBeneficiaryChanged(event.target.value));
      setError(null);
    },
    [dispatch],
  );

  const onCurrencyChange = React.useCallback(
    (next: CurrencyCodeType): void => {
      dispatch(currencyChanged(next));
      setError(null);
    },
    [dispatch],
  );

  const onFromAmountChange = React.useCallback(
    (value: number | null): void => {
      if (error === FormErrors.MISSING_AMOUNT || error === FormErrors.AMOUNT_BIGGER_THAN_BALANCE) {
        setError(null);
      }
      dispatch(fromAmountChanged(value));
    },
    [dispatch, error],
  );

  const onToAmountChange = React.useCallback(
    (value: number | null): void => {
      if (error === FormErrors.MISSING_AMOUNT || error === FormErrors.AMOUNT_BIGGER_THAN_BALANCE) {
        setError(null);
      }
      dispatch(toAmountChanged(value));
    },
    [dispatch, error],
  );

  const onSetMaxAmount = React.useCallback((): void => {
    if (currentBalance && currentBalance.available > 0) {
      onFromAmountChange(currentBalance.available);
    }
  }, [currentBalance, onFromAmountChange]);

  const [simulation, fetchingSimulation] = useSimulation({
    fromAmount: fromAmount,
    toAmount: toAmount,
    simulationId: null,
    fromCurrency: currency,
    toCurrency: currency,
    fundingMethod: PaymentRailType.CalizaWallet,
    beneficiaryId: sourceBeneficiaryId || undefined,
    targetBeneficiaryId: destinationBeneficiaryId || undefined,
  });

  React.useEffect((): void => {
    if (simulation.from.value !== null && simulation.to.value !== null) {
      dispatch(simulationCreated(simulation));
    }
  }, [dispatch, simulation]);

  const [createTransaction, mutation] = useCreateTransactionMutation();

  const fundsOverflow = React.useMemo((): boolean => {
    if (fromAmount === null || !currentBalance) {
      return false;
    }
    return Math.round(100 * currentBalance.available) < Math.round(100 * fromAmount);
  }, [fromAmount, currentBalance]);

  const handleConfirm = React.useCallback((): void => {
    if (!direction) {
      return setError(FormErrors.MISSING_DIRECTION);
    }
    if (!customerBeneficiaryId) {
      return setError(FormErrors.MISSING_CUSTOMER);
    }
    if (currency === CurrencyCodeType.UNSET) {
      return setError(FormErrors.MISSING_CURRENCY);
    }
    if (!fromAmount) {
      return setError(FormErrors.MISSING_AMOUNT);
    }
    if (fundsOverflow) {
      return setError(FormErrors.AMOUNT_BIGGER_THAN_BALANCE);
    }
    if (!simulation.id) {
      return;
    }

    createTransaction({
      simulationId: simulation.id,
      beneficiaryIp: '',
      marketExecutionType: MarketExecutionType.executeImmediately,
    });

    setTimeout((): void => {
      globalDispatch(api.util.invalidateTags([{ type: 'Balances', id: currency }]));
    }, 2000);
  }, [
    direction,
    customerBeneficiaryId,
    currency,
    fromAmount,
    fundsOverflow,
    simulation.id,
    createTransaction,
    globalDispatch,
  ]);

  const handleTransactionSuccess = React.useCallback(
    (_response: TransactionResponse): void => {
      navigate('completed');
    },
    [navigate],
  );

  React.useEffect((): void => {
    if (mutation.status === QueryStatus.fulfilled) {
      mutation.reset();
      handleTransactionSuccess(mutation.data);
    }
    if (mutation.status === QueryStatus.rejected) {
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
      mutation.reset();
    }
  }, [mutation, handleTransactionSuccess, toast, tr]);

  const onClose = React.useCallback((): void => {
    navigate('/home');
  }, [navigate]);

  const creatingTransaction = mutation.status === QueryStatus.pending;
  const loadingBeneficiaries = !fetchedBeneficiariesPage;

  const showCustomerSection = direction !== null;
  const showCurrencySection = showCustomerSection && customerBeneficiaryId !== '';
  const showAmountSection = showCurrencySection && currency !== CurrencyCodeType.UNSET;
  const showDetailsSection =
    showAmountSection && (fromAmount !== null || toAmount !== null) && !!simulation.id;

  const amountHelperText = React.useMemo(
    (): string | null =>
      match(error)
        .with(FormErrors.MISSING_AMOUNT, (): string => tr('REQUIRED_AMOUNT'))
        .with(FormErrors.AMOUNT_BIGGER_THAN_BALANCE, (): string =>
          tr(
            'AMOUNT_BIGGER_THAN_BALANCE',
            currentBalance
              ? formatter.format(currentBalance.available, currentBalance.currencyCode)
              : '',
          ),
        )
        .otherwise((): null => null),
    [error, tr, formatter, currentBalance],
  );

  const canConfirm = React.useMemo((): boolean => {
    return (
      !!direction &&
      !!customerBeneficiaryId &&
      currency !== CurrencyCodeType.UNSET &&
      !!fromAmount &&
      !fundsOverflow &&
      !!simulation.id &&
      !fetchingSimulation
    );
  }, [
    direction,
    customerBeneficiaryId,
    currency,
    fromAmount,
    fundsOverflow,
    simulation.id,
    fetchingSimulation,
  ]);

  return (
    <ApplicationDialog fullScreen={true}>
      <Header title="INTERNAL_TRANSFER" onClose={onClose} />
      <FullScreenModalContainer>
        <Section
          title={tr('INTERNAL_TRANSFER_DIRECTION')}
          visible={true}
          working={creatingTransaction}
          spotlight={true}>
          <FormControl>
            <RadioGroup value={direction ?? ''} onChange={onDirectionChange} sx={styles.radioGroup}>
              <FormControlLabel
                value="main_to_customer"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">{tr('INTERNAL_TRANSFER_TO_CUSTOMER')}</Typography>
                    <Typography variant="body1" color="custom.LightGray">
                      {tr('INTERNAL_TRANSFER_TO_CUSTOMER_DESCRIPTION')}
                    </Typography>
                  </Box>
                }
                sx={styles.radioLabel}
              />
              <FormControlLabel
                value="customer_to_main"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2">{tr('INTERNAL_TRANSFER_FROM_CUSTOMER')}</Typography>
                    <Typography variant="body1" color="custom.LightGray">
                      {tr('INTERNAL_TRANSFER_FROM_CUSTOMER_DESCRIPTION')}
                    </Typography>
                  </Box>
                }
                sx={styles.radioLabel}
              />
            </RadioGroup>
          </FormControl>
        </Section>

        <Section
          title={tr('SELECT_CUSTOMER')}
          visible={showCustomerSection}
          working={creatingTransaction || loadingBeneficiaries}
          spotlight={true}>
          <FormControl fullWidth={true}>
            <InputLabel id="internal-transfer-customer-label">{tr('SELECT_CUSTOMER')}</InputLabel>
            <Select
              labelId="internal-transfer-customer-label"
              id="internal-transfer-customer-select"
              value={customerBeneficiaryId}
              label={tr('SELECT_CUSTOMER')}
              onChange={onCustomerChange}
              error={error === FormErrors.MISSING_CUSTOMER}
              MenuProps={{
                disablePortal: false,
                disableAutoFocusItem: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              }}>
              <SearchMenuItem onChange={onSearchKeywordChange} />
              {filteredBeneficiaries.map(
                (filtered: FilteredBeneficiary): React.ReactElement => (
                  <MenuItem key={filtered.item.id} value={filtered.item.id}>
                    <HighlightedText text={filtered.item.name} indices={filtered.matchIndices} />
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Section>

        <Section
          title={tr('SELECT_FUNDING_CURRENCY')}
          visible={showCurrencySection}
          working={creatingTransaction}
          spotlight={true}>
          <Stack gap={2}>
            {SUPPORTED_CURRENCIES.map((currencyCode: CurrencyCodeType): React.ReactElement => {
              const balance = sourceBalances[currencyCode] ?? DetailedBalance.zero(currencyCode);
              const currencyMeta = Currency.fromCode(locale, currencyCode, displayUsdcAsUsd);
              const Icon = currencyMeta.icon;
              const isSelected = currency === currencyCode;
              const noFunds = balance.available <= 0;
              return (
                <ButtonBase
                  key={currencyCode}
                  onClick={(): void => onCurrencyChange(currencyCode)}
                  disabled={noFunds}
                  sx={[
                    styles.currencyOption,
                    isSelected ? styles.currencyOptionSelected : {},
                    noFunds ? styles.currencyOptionDisabled : {},
                  ]}>
                  <Box sx={styles.currencyIcon}>
                    <Icon width={24} height={24} />
                  </Box>
                  <Box sx={styles.currencyInfo}>
                    <Typography variant="body1" fontWeight={500}>
                      {currencyMeta.name}
                    </Typography>
                    <Typography variant="body2" color="custom.LightGray">
                      {tr('BALANCE')}: {formatter.format(balance.available, currencyCode)}
                    </Typography>
                  </Box>
                </ButtonBase>
              );
            })}
          </Stack>
        </Section>

        <Section
          title={tr('TRANSACTION_DETAILS')}
          visible={showAmountSection}
          working={creatingTransaction || fetchingSimulation}
          spotlight={true}>
          <Box display="flex" flexDirection="column" gap={2}>
            <AmountAndCurrencyWidget
              name="sendAmount"
              size="normal"
              label={tr(direction === 'customer_to_main' ? 'YOU_PULL_FROM_CUSTOMER' : 'YOU_SEND')}
              currencies={[currency]}
              amount={fromAmount}
              currency={currency}
              currencyDisabled={true}
              disabled={creatingTransaction}
              helperText={amountHelperText}
              error={!!amountHelperText}
              onAmountChange={debounce(onFromAmountChange, 300)}
              onCurrencyChange={(): void => {}}
            />
            {currentBalance && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" color="custom.LightGray">
                  {tr('BALANCE')}:{' '}
                  {formatter.format(currentBalance.available, currentBalance.currencyCode)}
                </Typography>
                <Button
                  variant="dark"
                  size="small"
                  sx={styles.maxButton}
                  disabled={creatingTransaction || currentBalance.available <= 0}
                  onClick={onSetMaxAmount}>
                  {tr('MAX')}
                </Button>
              </Box>
            )}
            <AmountAndCurrencyWidget
              name="receiveAmount"
              size="normal"
              label={tr(direction === 'customer_to_main' ? 'YOU_RECEIVE' : 'CUSTOMER_GETS')}
              currencies={[currency]}
              amount={toAmount}
              currency={currency}
              currencyDisabled={true}
              disabled={creatingTransaction}
              onAmountChange={debounce(onToAmountChange, 300)}
              onCurrencyChange={(): void => {}}
            />
            {showDetailsSection && (
              <StyledAccordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Typography variant="h7">{tr('TRANSACTION_DETAILS_TITLE')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TransactionDetails
                    transactionDetails={simulation.transactionDetails}
                    fromAmount={
                      simulation.from.value !== null ? Number(simulation.from.value) : fromAmount
                    }
                    fromCurrency={currency}
                    toAmount={simulation.to.value !== null ? Number(simulation.to.value) : toAmount}
                    toCurrency={currency}
                  />
                </AccordionDetails>
              </StyledAccordion>
            )}
          </Box>

          <Box sx={styles.expectedSettlementTimeInfo}>
            <ExpectedSettlementTimeInfo expectedSettlementTimeDetails={INSTANT_SETTLEMENT} />
          </Box>
        </Section>

        <Box width={552} mx="auto">
          <LoadingButton
            variant="contained"
            color="primary"
            fullWidth={true}
            loading={creatingTransaction}
            disabled={!canConfirm}
            onClick={handleConfirm}>
            {tr('CONFIRM_INTERNAL_TRANSFER')}
          </LoadingButton>
        </Box>
      </FullScreenModalContainer>

      <Outlet />
    </ApplicationDialog>
  );
};

const StyledAccordion = styled(Accordion)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    border: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.custom.DividerLight,
    borderRadius: 16,
    '&::before': {
      display: 'none',
    },
    '&:last-of-type': {
      borderRadius: 16,
    },
  }),
);

const styles: Record<string, SystemStyleObject> = {
  radioGroup: {
    gap: 2,
  },
  radioLabel: {
    alignItems: 'center',
    color: 'text.primary',
  },
  currencyOption: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    padding: 2,
    borderRadius: 2,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  currencyOptionSelected: {
    borderColor: 'primary.main',
    backgroundColor: 'custom.Primary1Background',
  },
  currencyOptionDisabled: {
    opacity: 0.5,
  },
  currencyIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
  },
  currencyInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
  },
  expectedSettlementTimeInfo: {
    display: 'flex',
    justifyContent: 'center',
    mt: 3,
    color: 'primary.main',
  },
  maxButton: {
    padding: 0,
    width: 53,
    minWidth: 0,
  },
};
